import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MemberHeader from "@/components/MemberHeader";
import AuthImage from "@/components/AuthImage";
import SuggestPlanDialog from "@/components/SuggestPlanDialog";
import { SupportChat } from "@/components/SupportChat";
import { ChangePasswordForm } from "@/components/ChangePasswordForm";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Check, X, Clock, ListChecks, Users, Sparkles, MessageSquare, AlertTriangle, Plus, Tag, Trash2, FileText, Ban, Unlock, UserX, CheckCircle2, XCircle, Crown, KeyRound, Cake, Copy } from "lucide-react";

function isBlocked(m) {
  if (!m.blocked_until) return false;
  try { return new Date(m.blocked_until) > new Date(); } catch { return false; }
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);

  const loadStats = async () => {
    const [{ data: s }, { data: c }] = await Promise.all([api.get("/admin/stats"), api.get("/plans/categories")]);
    setStats(s);
    setCategories(c);
  };

  useEffect(() => { loadStats(); }, []);

  const pct = stats ? Math.min(100, Math.round((stats.approved / stats.founding_limit) * 100)) : 0;

  return (
    <div className="min-h-screen sc-paper">
      <MemberHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <div className="sc-label text-[#48020C]">Painel de administração</div>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#26130E] mt-2">Gestão do Club</h1>
          </div>
          <div className="flex items-center gap-3">
            <Dialog>
              <DialogTrigger asChild>
                <button data-testid="admin-change-password-btn" className="inline-flex items-center gap-2 border border-[#48020C] text-[#48020C] hover:bg-[#F7EEC7] rounded-full px-5 py-3 text-sm transition-colors"><KeyRound className="w-4 h-4" /> Alterar password</button>
              </DialogTrigger>
              <DialogContent className="bg-[#FCFCFA] border-[#E3DED2] max-w-md">
                <DialogHeader><DialogTitle className="font-serif text-2xl text-[#26130E]">Alterar password</DialogTitle><DialogDescription className="sr-only">Alterar a password da conta de administração</DialogDescription></DialogHeader>
                <ChangePasswordForm />
              </DialogContent>
            </Dialog>
            <SuggestPlanDialog categories={categories} isAdmin onCreated={loadStats}
              trigger={<button data-testid="admin-create-plan-btn" className="inline-flex items-center gap-2 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full px-6 py-3 transition-colors"><Plus className="w-4 h-4" /> Criar plano</button>} />
          </div>
        </div>

        {/* Founding counter */}
        {stats && (
          <div data-testid="founding-counter" className="mt-8 bg-[#48020C] rounded-2xl px-6 py-5 text-[#FCFCFA] sc-shadow-soft">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-4">
                <div className="sc-label text-[#F7EEC7] flex items-center gap-2"><Crown className="w-3.5 h-3.5" /> Membros fundadoras</div>
                <div className="font-serif text-2xl">{stats.approved} <span className="text-[#F7EEC7]/70 text-lg">/ {stats.founding_limit}</span></div>
              </div>
              {stats.founding_reached && (
                <div data-testid="founding-alert" className="flex items-center gap-2 bg-[#F7EEC7] text-[#48020C] rounded-full px-4 py-1.5 text-sm font-medium">
                  <AlertTriangle className="w-4 h-4" /> Atingiste os 100 membros fundadoras!
                </div>
              )}
            </div>
            <div className="mt-3 h-1.5 bg-[#FCFCFA]/20 rounded-full overflow-hidden">
              <div className="h-full bg-[#F7EEC7] transition-all duration-700" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}

        {/* Stat cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Stat icon={Users} label="Aprovadas" value={stats.approved} />
            <Stat icon={Clock} label="Pendentes" value={stats.pending} />
            <Stat icon={ListChecks} label="Waiting list" value={stats.waitlist} />
            <Stat icon={Sparkles} label="Planos por aprovar" value={stats.pending_plans} />
          </div>
        )}

        {/* Attendance panel */}
        {stats?.attendance && (
          <div data-testid="attendance-panel" className="mt-6 sc-card px-6 py-5">
            <div className="flex items-center gap-2 sc-label text-[#48020C]">
              <CheckCircle2 className="w-4 h-4" /> Painel de presenças · {stats.attendance.past_events} eventos passados
            </div>
            {stats.attendance.total_rsvps > 0 ? (
              <>
                <div className="mt-4 flex flex-wrap items-end gap-x-10 gap-y-3">
                  <div data-testid="attendance-confirm-rate">
                    <div className="font-serif text-4xl text-[#2E5E3A]">{stats.attendance.confirm_rate}%</div>
                    <div className="text-sm text-[#6E5F58] mt-1">Confirmações · {stats.attendance.confirmed}</div>
                  </div>
                  <div data-testid="attendance-noshow-rate">
                    <div className="font-serif text-4xl text-[#8C2222]">{stats.attendance.no_show_rate}%</div>
                    <div className="text-sm text-[#6E5F58] mt-1">Faltas · {stats.attendance.no_shows}</div>
                  </div>
                  <div className="text-sm text-[#6E5F58] ml-auto">Total de inscrições: <strong className="text-[#26130E]">{stats.attendance.total_rsvps}</strong></div>
                </div>
                <div className="mt-4 h-2 bg-[#E3DED2] rounded-full overflow-hidden flex">
                  <div className="h-full bg-[#2E5E3A] transition-all duration-700" style={{ width: `${stats.attendance.confirm_rate}%` }} />
                  <div className="h-full bg-[#8C2222] transition-all duration-700" style={{ width: `${stats.attendance.no_show_rate}%` }} />
                </div>
              </>
            ) : (
              <p className="mt-3 text-sm text-[#6E5F58]">Ainda não há eventos passados para calcular presenças.</p>
            )}
          </div>
        )}

        <Tabs defaultValue="pending" className="mt-12">
          <TabsList className="bg-[#F4F1EA] border border-[#E3DED2] p-1 rounded-full flex-wrap h-auto">
            <TabsTrigger value="pending" data-testid="tab-pending" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Candidaturas{stats?.pending ? <span className="sc-count">{stats.pending}</span> : null}</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Waiting list{stats?.waitlist ? <span className="sc-count">{stats.waitlist}</span> : null}</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Membros{stats?.approved ? <span className="sc-count">{stats.approved}</span> : null}</TabsTrigger>
            <TabsTrigger value="plans" data-testid="tab-plans" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Sugestões de planos{stats?.pending_plans ? <span className="sc-count">{stats.pending_plans}</span> : null}</TabsTrigger>
            <TabsTrigger value="chats" data-testid="tab-chats" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Chats & inscritas</TabsTrigger>
            <TabsTrigger value="suporte" data-testid="tab-suporte" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Suporte{stats?.support_unread ? <span className="sc-count">{stats.support_unread}</span> : null}</TabsTrigger>
            <TabsTrigger value="faltas" data-testid="tab-faltas" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Faltas</TabsTrigger>
            <TabsTrigger value="aniversarios" data-testid="tab-aniversarios" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Aniversários</TabsTrigger>
            <TabsTrigger value="categorias" data-testid="tab-categorias" className="rounded-full data-[state=active]:bg-[#48020C] data-[state=active]:text-[#FCFCFA]">Categorias</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-8"><MembersList status="pending" onChange={loadStats} /></TabsContent>
          <TabsContent value="waitlist" className="mt-8"><MembersList status="waitlist" onChange={loadStats} /></TabsContent>
          <TabsContent value="approved" className="mt-8"><MembersList status="approved" onChange={loadStats} /></TabsContent>
          <TabsContent value="plans" className="mt-8"><PendingPlans onChange={loadStats} /></TabsContent>
          <TabsContent value="chats" className="mt-8"><ChatsAndAttendees /></TabsContent>
          <TabsContent value="suporte" className="mt-8"><SupportThreads onChanged={loadStats} /></TabsContent>
          <TabsContent value="faltas" className="mt-8"><NoShowsPanel /></TabsContent>
          <TabsContent value="aniversarios" className="mt-8"><BirthdaysPanel /></TabsContent>
          <TabsContent value="categorias" className="mt-8"><CategoriesManager onChange={loadStats} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="sc-card sc-card-hover p-6">
      <div className="w-10 h-10 rounded-full bg-[#48020C]/10 flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#48020C]" />
      </div>
      <div className="font-serif text-4xl text-[#26130E] mt-4">{value}</div>
      <div className="sc-label mt-1">{label}</div>
    </div>
  );
}

function MembersList({ status, onChange }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/members", { params: { status } });
    setMembers(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/members/${id}/${action}`);
      toast.success(action === "approve" ? "Membro aprovado!" : action === "waitlist" ? "Movida para waiting list." : "Candidatura rejeitada.");
      load();
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <p className="text-[#6E6359]">A carregar…</p>;
  if (members.length === 0) return <p className="text-[#6E6359] py-8">Sem registos aqui.</p>;

  return (
    <div className="space-y-3">
      {members.map((m) => (
        <div key={m.id} data-testid={`member-row-${m.id}`} className="sc-card sc-card-hover p-5 flex items-center gap-4 flex-wrap">
          <AuthImage path={m.photo_path} fallbackName={m.name} className="w-14 h-14 rounded-full object-cover border border-[#48020C]" />
          <div className="flex-1 min-w-[180px]">
            <div className="font-serif text-xl text-[#26130E]">{m.name} {m.is_founding && <span className="text-xs text-[#48020C]">· Fundadora</span>}{isBlocked(m) && <span className="text-xs text-[#8C2222] ml-1">· Bloqueada</span>}</div>
            <div className="text-sm text-[#6E6359]">{m.profession} · {m.email}</div>
            {m.motivation && <div className="text-sm text-[#6E5F58] italic mt-2 sc-quote">{m.motivation}</div>}
          </div>
          <div className="flex gap-2 flex-wrap">
            <ApplicationDialog member={m} />
            <ResetLinkDialog member={m} />
            {status !== "approved" && (
              <button data-testid={`approve-${m.id}`} onClick={() => act(m.id, "approve")} className="flex items-center gap-1.5 bg-[#1E5E2A] hover:bg-[#164a20] text-white rounded-full px-4 py-2 text-sm transition-colors"><Check className="w-4 h-4" /> Aprovar</button>
            )}
            {status !== "waitlist" && (
              <button data-testid={`waitlist-${m.id}`} onClick={() => act(m.id, "waitlist")} className="flex items-center gap-1.5 border border-[#48020C] text-[#8A5B00] hover:bg-[#FEF7E7] rounded-full px-4 py-2 text-sm transition-colors"><ListChecks className="w-4 h-4" /> Waiting list</button>
            )}
            <button data-testid={`reject-${m.id}`} onClick={() => act(m.id, "reject")} className="flex items-center gap-1.5 border border-[#8C2222] text-[#8C2222] hover:bg-[#F9ECEC] rounded-full px-4 py-2 text-sm transition-colors"><X className="w-4 h-4" /> Rejeitar</button>
            {status === "approved" && (
              isBlocked(m) ? (
                <button data-testid={`unblock-${m.id}`} onClick={() => act(m.id, "unblock")} className="flex items-center gap-1.5 border border-[#48020C] text-[#48020C] hover:bg-[#F7EEC7] rounded-full px-4 py-2 text-sm transition-colors"><Unlock className="w-4 h-4" /> Desbloquear</button>
              ) : (
                <button data-testid={`block-${m.id}`} onClick={() => act(m.id, "block")} className="flex items-center gap-1.5 border border-[#8C2222] text-[#8C2222] hover:bg-[#F9ECEC] rounded-full px-4 py-2 text-sm transition-colors"><Ban className="w-4 h-4" /> Bloquear 30 dias</button>
              )
            )}
            </div>

        </div>
      ))}
    </div>
  );
}

function PendingPlans({ onChange }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/plans/pending");
    setPlans(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    await api.post(`/admin/plans/${id}/${action}`);
    toast.success(action === "approve" ? "Plano aprovado!" : "Plano rejeitado.");
    load();
    onChange();
  };

  if (loading) return <p className="text-[#6E6359]">A carregar…</p>;
  if (plans.length === 0) return <p className="text-[#6E6359] py-8">Não há sugestões pendentes.</p>;

  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {plans.map((p) => (
        <div key={p.id} data-testid={`pending-plan-${p.id}`} className="sc-card sc-card-hover p-6">
          <span className="sc-label text-[#48020C]">{p.category}</span>
          <h3 className="font-serif text-2xl text-[#26130E] mt-1">{p.title}</h3>
          <p className="text-sm text-[#6E6359] mt-2">{p.date}{p.time ? ` · ${p.time}` : ""} · {p.location}</p>
          <p className="text-sm text-[#6E6359]">{p.seats} lugares · {p.value ? `${p.value} €` : "Gratuito"} · sugerido por {p.created_by_name}</p>
          {p.description && <p className="text-sm text-[#6E5F58] mt-2">{p.description}</p>}
          <div className="flex gap-2 mt-4">
            <button data-testid={`approve-plan-${p.id}`} onClick={() => act(p.id, "approve")} className="flex items-center gap-1.5 bg-[#1E5E2A] hover:bg-[#164a20] text-white rounded-full px-4 py-2 text-sm transition-colors"><Check className="w-4 h-4" /> Aprovar</button>
            <button data-testid={`reject-plan-${p.id}`} onClick={() => act(p.id, "reject")} className="flex items-center gap-1.5 border border-[#8C2222] text-[#8C2222] hover:bg-[#F9ECEC] rounded-full px-4 py-2 text-sm transition-colors"><X className="w-4 h-4" /> Rejeitar</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function ChatsAndAttendees() {
  const [chats, setChats] = useState([]);
  const load = () => api.get("/admin/chats").then(({ data }) => setChats(data));
  useEffect(() => { load(); }, []);

  const removePlan = async (id) => {
    try {
      await api.delete(`/admin/plans/${id}`);
      toast.success("Plano apagado.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível apagar o plano.");
    }
  };

  if (chats.length === 0) return <p className="text-[#6E6359] py-8">Ainda não há planos aprovados.</p>;

  return (
    <div className="space-y-3">
      {chats.map((c) => (
        <div key={c.id} className="sc-card sc-card-hover p-5 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <span className="sc-label text-[#48020C]">{c.category}</span>
            <div className="font-serif text-xl text-[#26130E]">{c.title}</div>
            <div className="text-sm text-[#6E6359]">{c.date} · {c.message_count} mensagens</div>
          </div>
          <div className="flex gap-2">
            <AttendeesDialog planId={c.id} title={c.title} date={c.date} />
            <a href={`/area/plano/${c.id}`} data-testid={`open-chat-${c.id}`} className="flex items-center gap-1.5 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full px-4 py-2 text-sm transition-colors"><MessageSquare className="w-4 h-4" /> Ver chat</a>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid={`delete-plan-${c.id}`} className="flex items-center gap-1.5 border border-[#8C2222] text-[#8C2222] hover:bg-[#F9ECEC] rounded-full px-4 py-2 text-sm transition-colors"><Trash2 className="w-4 h-4" /> Apagar</button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-[#FCFCFA] border-[#E3DED2]">
                <AlertDialogHeader>
                  <AlertDialogTitle className="font-serif text-2xl text-[#26130E]">Apagar plano?</AlertDialogTitle>
                  <AlertDialogDescription className="text-[#6E5F58]">Vais apagar <strong>{c.title}</strong> ({c.date}). As inscrições e o chat deste plano serão removidos. Esta ação é irreversível.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-full border-[#E3DED2]">Cancelar</AlertDialogCancel>
                  <AlertDialogAction data-testid={`confirm-delete-plan-${c.id}`} onClick={() => removePlan(c.id)} className="rounded-full bg-[#8C2222] hover:bg-[#6f1a1a] text-white">Apagar plano</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      ))}
    </div>
  );
}

function BirthdaysPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const DAYS = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
  useEffect(() => {
    (async () => {
      setLoading(true);
      try { const { data } = await api.get("/admin/birthdays"); setRows(data); } catch { /* noop */ }
      setLoading(false);
    })();
  }, []);

  if (loading) return <p className="text-[#6E6359]">A carregar…</p>;

  return (
    <div>
      <p className="text-sm text-[#6E5F58] mb-4">Aniversários dos membros nesta semana. Cada membro recebe automaticamente um email de parabéns no seu dia. 🎂</p>
      {rows.length === 0 ? (
        <p className="text-[#6E6359] py-8">Ninguém faz anos esta semana. ✦</p>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={`${r.name}-${r.date}`} data-testid={`birthday-${r.date}`} className={`sc-card p-5 flex items-center justify-between gap-4 flex-wrap ${r.is_today ? "ring-1 ring-[#48020C]" : ""}`}>
              <div className="flex items-center gap-3">
                <AuthImage path={r.photo_path} fallbackName={r.name} className="w-11 h-11 rounded-full object-cover border border-[#48020C] flex-shrink-0" />
                <div>
                  <div className="font-serif text-lg text-[#26130E]">{r.name}{r.is_today && <span className="ml-2 text-xs font-semibold text-[#8A5B00] bg-[#FEF7E7] px-2 py-0.5 rounded-full">Hoje 🎉</span>}</div>
                  <div className="text-sm text-[#6E6359]">{DAYS[r.weekday]} · {r.date.slice(8, 10)}/{r.date.slice(5, 7)} · faz {r.turning} anos</div>
                </div>
              </div>
              <Cake className="w-5 h-5 text-[#48020C]" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function SupportThreads({ onChanged }) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/support"); setThreads(data); } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const markRead = async (uid) => {
    try { await api.post(`/admin/support/${uid}/read`); await load(); onChanged?.(); } catch { /* noop */ }
  };

  if (loading) return <p className="text-[#6E6359]">A carregar…</p>;
  if (threads.length === 0) return <p className="text-[#6E6359] py-8">Ainda não há conversas de apoio. Quando uma membro escrever, aparece aqui.</p>;

  return (
    <div className="space-y-3">
      {threads.map((t) => (
        <div key={t.thread_id} data-testid={`support-thread-${t.user_id}`} className="sc-card sc-card-hover p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <AuthImage path={t.photo_path} fallbackName={t.user_name} className="w-11 h-11 rounded-full object-cover border border-[#48020C] flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-serif text-lg text-[#26130E] flex items-center gap-2">{t.user_name}{t.unread > 0 && <span data-testid={`support-unread-${t.user_id}`} className="inline-flex items-center justify-center min-w-[1.3rem] h-[1.3rem] px-1.5 text-[0.66rem] font-bold rounded-full bg-[#8C2222] text-white">{t.unread}</span>}</div>
              <div className="text-sm text-[#6E6359] truncate max-w-xs">{t.last_text || "—"}</div>
            </div>
          </div>
          <Dialog onOpenChange={(o) => { if (o) markRead(t.user_id); }}>
            <DialogTrigger asChild>
              <button data-testid={`open-support-${t.user_id}`} className="flex items-center gap-1.5 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full px-4 py-2 text-sm transition-colors"><MessageSquare className="w-4 h-4" /> Responder</button>
            </DialogTrigger>
            <DialogContent className="bg-[#FCFCFA] border-[#E3DED2] max-w-lg">
              <DialogHeader><DialogTitle className="font-serif text-2xl text-[#26130E]">Apoio · {t.user_name}</DialogTitle><DialogDescription className="sr-only">Conversa de apoio com {t.user_name}</DialogDescription></DialogHeader>
              <SupportChat threadId={t.thread_id} heightClass="h-[58vh]" hideHeader />
            </DialogContent>
          </Dialog>
        </div>
      ))}
    </div>
  );
}

function AttendeesDialog({ planId, title, date }) {
  const [list, setList] = useState([]);
  const isPast = date && date < new Date().toISOString().slice(0, 10);
  const load = async () => {
    const { data } = await api.get(`/admin/plans/${planId}/attendees`);
    setList(data);
  };
  const block = async (userId, name) => {
    try {
      await api.post(`/admin/members/${userId}/block`);
      toast.success(`${name} bloqueada por 30 dias.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };
  const confirmedCount = list.filter((a) => a.confirmed).length;
  return (
    <Dialog onOpenChange={(o) => o && load()}>
      <DialogTrigger asChild>
        <button data-testid={`attendees-${planId}`} className="flex items-center gap-1.5 border border-[#48020C] text-[#8A5B00] hover:bg-[#FEF7E7] rounded-full px-4 py-2 text-sm transition-colors"><Users className="w-4 h-4" /> Inscritas</button>
      </DialogTrigger>
      <DialogContent className="bg-[#FCFCFA] border-[#E3DED2] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#26130E]">Inscritas · {title}</DialogTitle>
        </DialogHeader>
        {list.length === 0 ? <p className="text-[#6E6359]">Ninguém inscrito ainda.</p> : (
          <>
            <p className="text-sm text-[#6E5F58]">{confirmedCount} de {list.length} confirmaram presença{isPast ? " · evento já decorreu" : ""}</p>
            <ul className="space-y-2 mt-3">
              {list.map((a) => {
                const noShow = isPast && !a.confirmed;
                return (
                  <li key={a.user_id} data-testid={`attendee-${a.user_id}`} className="flex items-center justify-between gap-3 bg-[#F4F1EA] rounded-xl px-4 py-3 flex-wrap">
                    <span className="text-[#26130E]">{a.user_name}</span>
                    <div className="flex items-center gap-2">
                      {a.confirmed ? (
                        <span data-testid={`confirmed-${a.user_id}`} className="text-xs font-semibold px-3 py-1 rounded-full bg-[#EBF4EC] text-[#1E5E2A]">Confirmada</span>
                      ) : noShow ? (
                        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F9ECEC] text-[#8C2222]">Faltou sem avisar</span>
                      ) : (
                        <span className="text-xs font-semibold px-3 py-1 rounded-full bg-[#F7EEC7] text-[#8A5B00]">Sem resposta</span>
                      )}
                      {noShow && (
                        <button data-testid={`block-noshow-${a.user_id}`} onClick={() => block(a.user_id, a.user_name)} className="flex items-center gap-1.5 border border-[#8C2222] text-[#8C2222] hover:bg-[#F9ECEC] rounded-full px-3 py-1 text-xs transition-colors"><Ban className="w-3.5 h-3.5" /> Bloquear 30 dias</button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


const LOOKING_LABEL = "O que procura";
const APP_QUESTIONS = [
  ["q1_brought", "O que a trouxe ao Club"],
  ["q2_looking_for", "O que procura na comunidade"],
  ["q3_plan_types", "Tipos de planos preferidos"],
  ["q4_scenario", "Sozinha num evento faria…"],
  ["q5_good_friend", "O que faz uma boa amiga"],
  ["q6_contribution", "O que pode trazer ao Club"],
  ["q7_friday", "Friday night plan"],
];

function ApplicationDialog({ member }) {
  const app = member.application || {};
  const [hist, setHist] = useState(null);
  const val = (v) => Array.isArray(v) ? (v.length ? v.join(" · ") : "—") : (v || "—");
  const loadHist = async () => {
    try { const { data } = await api.get(`/admin/members/${member.id}/history`); setHist(data); } catch {}
  };
  return (
    <Dialog onOpenChange={(o) => o && loadHist()}>
      <DialogTrigger asChild>
        <button data-testid={`application-${member.id}`} className="flex items-center gap-1.5 border border-[#E3DED2] text-[#48020C] hover:bg-[#ECE7DC] rounded-full px-4 py-2 text-sm transition-colors"><FileText className="w-4 h-4" /> Candidatura</button>
      </DialogTrigger>
      <DialogContent className="bg-[#FCFCFA] border-[#E3DED2] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-[#26130E]">Candidatura · {member.name}</DialogTitle></DialogHeader>
        {hist && (
          <div data-testid={`history-${member.id}`} className="grid grid-cols-3 gap-2 mt-2">
            <HistStat label="Confirmou" value={hist.confirmed} color="#1E5E2A" />
            <HistStat label="Cancelou" value={hist.cancelled} color="#8A5B00" />
            <HistStat label="Faltou" value={hist.no_show} color="#8C2222" />
          </div>
        )}
        <div className="space-y-4 mt-2 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <Detail k="Cidade" v={member.city} />
            <Detail k="Profissão" v={member.profession} />
            <Detail k="Nascimento" v={member.birth_date} />
            <Detail k="Instagram" v={member.instagram} />
            <Detail k="Email" v={member.email} />
            <Detail k="Telemóvel" v={member.phone} />
          </div>
          {APP_QUESTIONS.map(([k, label]) => (
            <div key={k} className="border-t border-[#E3DED2] pt-3">
              <div className="sc-label text-[#48020C]">{label}</div>
              <p className="text-[#3A1F19] mt-1">{val(app[k])}</p>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Detail({ k, v }) {
  return (
    <div>
      <div className="sc-label text-[#6E5F58]">{k}</div>
      <div className="text-[#26130E]">{v || "—"}</div>
    </div>
  );
}

function ResetLinkDialog({ member }) {
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/admin/members/${member.id}/reset-link`);
      setLink(data.link);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Não foi possível gerar o link.");
    } finally {
      setLoading(false);
    }
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado ✦");
    } catch {
      toast.error("Não foi possível copiar. Copia manualmente.");
    }
  };
  return (
    <Dialog onOpenChange={(o) => { if (!o) setLink(""); }}>
      <DialogTrigger asChild>
        <button data-testid={`reset-link-${member.id}`} className="flex items-center gap-1.5 border border-[#E3DED2] text-[#48020C] hover:bg-[#ECE7DC] rounded-full px-4 py-2 text-sm transition-colors"><KeyRound className="w-4 h-4" /> Link de password</button>
      </DialogTrigger>
      <DialogContent className="bg-[#FCFCFA] border-[#E3DED2] max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#26130E]">Recuperar password · {member.name}</DialogTitle>
          <DialogDescription className="text-[#6E5F58]">Gera um link manual para esta cliente definir uma nova password. Envia-lho por outro meio (ex: WhatsApp). Válido durante 2 horas e utilizável uma única vez.</DialogDescription>
        </DialogHeader>
        <div className="mt-2 space-y-4">
          <div className="text-sm text-[#6E6359]">{member.email}</div>
          {!link ? (
            <button data-testid={`reset-link-generate-${member.id}`} onClick={generate} disabled={loading} className="w-full bg-[#48020C] hover:bg-[#26130E] disabled:opacity-60 text-[#FCFCFA] font-medium rounded-full py-3 transition-colors">
              {loading ? "A gerar…" : "Gerar link de recuperação"}
            </button>
          ) : (
            <div className="space-y-3">
              <div data-testid={`reset-link-value-${member.id}`} className="break-all text-sm bg-[#F4F1EA] border border-[#E3DED2] rounded-xl p-3 text-[#26130E]">{link}</div>
              <div className="flex gap-2">
                <button data-testid={`reset-link-copy-${member.id}`} onClick={copy} className="flex-1 flex items-center justify-center gap-2 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full py-3 text-sm transition-colors"><Copy className="w-4 h-4" /> Copiar link</button>
                <button data-testid={`reset-link-regenerate-${member.id}`} onClick={generate} disabled={loading} className="border border-[#E3DED2] text-[#48020C] hover:bg-[#ECE7DC] rounded-full px-4 py-3 text-sm transition-colors">Gerar novo</button>
              </div>
              <p className="text-xs text-[#8A5B00]">⚠️ Gerar um novo link invalida qualquer link anterior ainda não usado.</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}function HistStat({ label, value, color }) {
  return (
    <div className="bg-[#F4F1EA] border border-[#E3DED2] rounded-xl p-3 text-center">
      <div className="font-serif text-2xl" style={{ color }}>{value}</div>
      <div className="sc-label mt-0.5">{label}</div>
    </div>
  );
}

function NoShowsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/no-shows"); setRows(data); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const block = async (userId, name) => {
    try {
      await api.post(`/admin/members/${userId}/block`);
      toast.success(`${name} bloqueada por 30 dias.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (planId, userId, name) => {
    try {
      await api.delete(`/admin/no-shows/${planId}/${userId}`);
      toast.success(`Falta de ${name} eliminada.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <p className="text-[#6E6359]">A carregar…</p>;
  if (rows.length === 0) return <p className="text-[#6E6359] py-8">Sem faltas por avisar — ninguém deixou de confirmar em eventos passados. ✦</p>;

  return (
    <div>
      <p className="text-sm text-[#6E5F58] mb-4">Membros que não confirmaram nem cancelaram presença em eventos que já decorreram. Podes bloquear 30 dias com um clique.</p>
      <div className="space-y-3">
        {rows.map((r) => (
          <div key={`${r.plan_id}-${r.user_id}`} data-testid={`noshow-${r.user_id}-${r.plan_id}`} className="sc-card sc-card-hover p-5 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <UserX className="w-5 h-5 text-[#8C2222]" />
              <div>
                <div className="font-serif text-lg text-[#26130E]">{r.user_name}</div>
                <div className="text-sm text-[#6E6359]">{r.plan_title} · {r.category} · {r.date}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button data-testid={`block-faltas-${r.user_id}`} onClick={() => block(r.user_id, r.user_name)} className="flex items-center gap-1.5 border border-[#8C2222] text-[#8C2222] hover:bg-[#F9ECEC] rounded-full px-4 py-2 text-sm transition-colors"><Ban className="w-4 h-4" /> Bloquear 30 dias</button>
              <button data-testid={`delete-faltas-${r.user_id}-${r.plan_id}`} onClick={() => remove(r.plan_id, r.user_id, r.user_name)} className="flex items-center gap-1.5 border border-[#6E5F58] text-[#6E5F58] hover:bg-[#ECE7DC] rounded-full px-4 py-2 text-sm transition-colors"><Trash2 className="w-4 h-4" /> Eliminar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function CategoriesManager({ onChange }) {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/categories");
    setCats(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await api.post("/admin/categories", { name: name.trim() });
      setName("");
      toast.success("Categoria adicionada.");
      load();
      onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/categories/${id}`);
    toast.success("Categoria removida.");
    load();
    onChange();
  };

  return (
    <div className="max-w-xl">
      <form onSubmit={add} className="flex gap-3 mb-6">
        <Input data-testid="new-category-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nova categoria (ex: Book Club)" className="bg-[#F4F1EA] border-[#E3DED2] rounded-xl" />
        <button type="submit" data-testid="add-category-btn" className="flex items-center gap-2 bg-[#48020C] hover:bg-[#26130E] text-[#FCFCFA] rounded-full px-6 py-2 transition-colors whitespace-nowrap"><Plus className="w-4 h-4" /> Adicionar</button>
      </form>
      {loading ? <p className="text-[#6E6359]">A carregar…</p> : (
        <ul className="space-y-2">
          {cats.map((c) => (
            <li key={c.id} data-testid={`category-row-${c.id}`} className="flex items-center justify-between bg-[#F4F1EA] border border-[#E3DED2] rounded-xl px-5 py-3">
              <span className="flex items-center gap-2 text-[#26130E]"><Tag className="w-4 h-4 text-[#48020C]" /> {c.name}</span>
              <button data-testid={`delete-category-${c.id}`} onClick={() => remove(c.id)} className="text-[#8C2222] hover:bg-[#F9ECEC] rounded-full p-2 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
